import React, { useState } from 'react';
import {
  Row,
  Container,
  Col,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from 'reactstrap';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import PropTypes from 'prop-types';
import Image from 'flaivy-react/components/Image';
import Link from 'next/link';

function Footer({ shopConfig }: { shopConfig: any }) {
  const {
    address,
    zipCode,
    city,
    aboutUs,
    instagram,
    facebook,
    linkedin,
    contactPhone,
    contactEmail,
    organization,
  } = shopConfig;

  const LogoCol = ({ shouldBeSideBySide }: { shouldBeSideBySide: boolean }) => (
    <Col className="mt-n3">
      <Image
        src="https://assets.flaivy.com/852a617073b5ca484075019efe44e411.png"
        alt={`${organization.name} Shop logo`}
        resX={175}
        resY={70}
      />
      <div>Privab Grossisterna AB</div>
      {shouldBeSideBySide ? (
        <Row className="my-2">
          <Col xs={6}>
            <div className="fw-bold mt-3">Postadress</div>
            <div>Box 195</div>
            <div>571 22 Nässjö</div>
          </Col>
          <Col xs={6}>
            <Link
              href="https://maps.app.goo.gl/rWppZZc3Wb8mbdtH7"
              target="_blank"
            >
              <div className="fw-bold mt-3">Besöksadress</div>
              <div>{address}</div>
              <div>
                {zipCode} {city}
              </div>
            </Link>
          </Col>
        </Row>
      ) : (
        <div>
          <div className="fw-bold mt-3">Postadress</div>
          <div>Box 195</div>
          <div>571 22 Nässjö</div>
          <div className="fw-bold mt-3">Besöksadress</div>
          <Link
            href="https://maps.app.goo.gl/rWppZZc3Wb8mbdtH7"
            target="_blank"
          >
            <div>{address}</div>
            <div>
              {zipCode} {city}
            </div>
          </Link>
        </div>
      )}
    </Col>
  );
  LogoCol.defaultProps = {
    shouldBeSideBySide: false,
  };

  const ContactHeader = () => (
    <div className="text-uppercase h6 fw-bold">Kontakt</div>
  );
  const ContactCol = ({
    noBorder,
    showHeader,
  }: {
    noBorder: boolean;
    showHeader: boolean;
  }) => (
    <Col
      className={
        noBorder
          ? ''
          : 'border border-light border-top-0 border-bottom-0 border-end-0 ps-5'
      }
    >
      {showHeader && <ContactHeader />}
      <ul className=" list-unstyled">
        <li>
          <Link href={aboutUs}>Om oss</Link>
        </li>
        <li>
          <Link href={'/register'}>Bli kund</Link>
        </li>
        <li>
          <Link href={'/become-supplier'}>Bli leverantör</Link>
        </li>
        <li>
          <Link href={'/integrity-policy'}>Integritetspolicy</Link>
        </li>
        <li>
          <Link href={'https://humblegroup.se/'} target="_blank">
            Humble
          </Link>
        </li>
        <li>
          <Link href={'/contact'}>Kontakt</Link>
        </li>
      </ul>
      <div className="fw-bold">
        Tel:{' '}
        <a href={`tel:${contactPhone}`} className=" text-decoration-none">
          {contactPhone}
        </a>
        <br />
        <a href={`mailto:${contactEmail}`} className=" text-decoration-none">
          {contactEmail}
        </a>
      </div>
    </Col>
  );
  ContactCol.defaultProps = {
    noBorder: false,
    showHeader: true,
  };

  const ConceptsHeader = () => (
    <div className="text-uppercase h6 fw-bold">Våra koncept</div>
  );
  const ConceptsCol = ({ showHeader }: { showHeader: boolean }) => (
    <Col>
      {showHeader && <ConceptsHeader />}
      <ul className=" list-unstyled">
        <li>
          <Link href={'/Gottmix'}>Gottmix</Link>
        </li>
        <li>
          <Link href={'/MyWay'}>MyWay</Link>
        </li>
        <li>
          <Link href={'/NRGFOOD'}>NRGFood</Link>
        </li>
      </ul>
    </Col>
  );
  ConceptsCol.defaultProps = {
    showHeader: true,
  };

  const LocationsHeader = () => (
    <div className="text-uppercase h6 fw-bold">Våra platser</div>
  );
  const LocationsCol = ({ showHeader }: { showHeader: boolean }) => (
    <Col>
      {showHeader && <LocationsHeader />}
      <ul className=" list-unstyled">
        <li>
          <Link
            href="https://maps.app.goo.gl/TkbxzpPPFb4emQK9A"
            target="_blank"
          >
            Umeå
          </Link>
        </li>
        <li>
          <Link
            href="https://maps.app.goo.gl/bPyddDyQW8mbrkHf8"
            target="_blank"
          >
            Trollhättan
          </Link>
        </li>
        <li>
          <Link
            href="https://maps.app.goo.gl/CnURCmQennMvRszu6"
            target="_blank"
          >
            Nässjö/Stockholm Syd
          </Link>
        </li>
        <li>
          <Link
            href="https://maps.app.goo.gl/fTWtraRE9mWFZvJA8"
            target="_blank"
          >
            Visby
          </Link>
        </li>
        <li>
          <Link
            href="https://maps.app.goo.gl/PJMpssLcxj9wDci87"
            target="_blank"
          >
            Helsingborg
          </Link>
        </li>
        <li>
          <Link
            href="https://maps.app.goo.gl/dzHb2c8amdpXxMSYA"
            target="_blank"
          >
            Ystad
          </Link>
        </li>
      </ul>
    </Col>
  );
  LocationsCol.defaultProps = {
    showHeader: true,
  };

  const SocialMediaHeader = () => (
    <div className="text-uppercase h6 fw-bold">Följ oss</div>
  );
  const SocialMediaCol = ({ showHeader }: { showHeader: boolean }) => (
    <Col>
      {showHeader && <SocialMediaHeader />}
      <div className="mt-1">
        <span className="me-2">
          <Link href={facebook}>
            {/* @ts-ignore */}
            <FontAwesomeIcon icon={faFacebookF} size="xl" color="white" />
          </Link>
        </span>
        <span className="mx-2">
          <Link href={instagram}>
            {/* @ts-ignore */}
            <FontAwesomeIcon icon={faInstagram} size="xl" color="white" />
          </Link>
        </span>
        <span className="mx-2">
          <Link href={linkedin}>
            {/* @ts-ignore */}
            <FontAwesomeIcon icon={faLinkedinIn} size="xl" color="white" />
          </Link>
        </span>
      </div>
    </Col>
  );
  SocialMediaCol.defaultProps = {
    showHeader: true,
  };

  const [open, setOpen] = useState('');
  const toggle = (id: string) => {
    if (open === id) {
      setOpen('');
    } else {
      setOpen(id);
    }
  };

  return (
    <footer className="py-5 px-4 bg-dark d-print-none mt-2">
      <Container>
        <Row className="d-none d-md-flex">
          <LogoCol />

          <ContactCol />

          <ConceptsCol />

          <LocationsCol />

          <SocialMediaCol />
        </Row>
        <Row className="d-md-none">
          <Col>
            <Accordion
              flush
              open={open}
              // reactstrap have forgotten to add toggle prop to Accordion types
              // @ts-ignore
              toggle={toggle}
              className="footer-accordion"
            >
              <AccordionItem>
                <AccordionHeader
                  targetId="Contact"
                  className="d-flex justify-content-between"
                >
                  <ContactHeader />
                  <FontAwesomeIcon
                    icon={open === 'Contact' ? faChevronUp : faChevronDown}
                    color="light"
                    className="float-end"
                  />
                </AccordionHeader>
                <AccordionBody accordionId="Contact">
                  <ContactCol noBorder showHeader={false} />
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader
                  targetId="Concepts"
                  className="d-flex justify-content-between"
                  tag="div"
                >
                  <ConceptsHeader />
                  <FontAwesomeIcon
                    icon={open === 'Concepts' ? faChevronUp : faChevronDown}
                    color="light"
                    className="float-end"
                  />
                </AccordionHeader>
                <AccordionBody accordionId="Concepts">
                  <ConceptsCol showHeader={false} />
                </AccordionBody>
              </AccordionItem>
              <AccordionItem>
                <AccordionHeader
                  targetId="Locations"
                  className="d-flex justify-content-between"
                >
                  <LocationsHeader />
                  <FontAwesomeIcon
                    icon={open === 'Locations' ? faChevronUp : faChevronDown}
                    color="light"
                    className="float-end"
                  />
                </AccordionHeader>
                <AccordionBody accordionId="Locations">
                  <LocationsCol showHeader={false} />
                </AccordionBody>
              </AccordionItem>
            </Accordion>
            <div className="mt-3">
              <LogoCol shouldBeSideBySide />
            </div>
            <div className="mt-3">
              <SocialMediaCol />
            </div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
}

Footer.propTypes = {
  shopConfig: PropTypes.object.isRequired,
};

export default Footer;
